import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import BrandLogo from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { QuestionInput, visibleFollowups, stepComplete } from "@/components/QuestionInput";
import { useQuestionnaire } from "@/context/QuestionnaireContext";

export function formatAnswer(q, v) {
  if (v === undefined || v === "" || v === null) return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  return String(v) + (q.unit ? ` ${q.unit}` : "");
}

const HINTS = {
  single: "Seleziona una risposta",
  multiple: "Seleziona una o più risposte",
  scale5: "Seleziona un valore sulla scala",
  scale: "Seleziona un valore sulla scala",
};

export default function Test() {
  const navigate = useNavigate();
  const { submissionId, answers, setAnswer, submit } = useQuestionnaire();
  const [schema, setSchema] = useState(null);
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!submissionId) navigate("/", { replace: true });
  }, [submissionId, navigate]);

  useEffect(() => {
    api.get("/questionnaire").then(({ data }) => setSchema(data)).catch(() => toast.error("Impossibile caricare il questionario."));
  }, []);

  // 35 official top-level questions (flattened, with their section titles)
  const steps = useMemo(() => {
    if (!schema) return [];
    const list = [];
    schema.sections.forEach((section) => {
      section.questions.forEach((q) => list.push({ ...q, sectionTitle: section.title, sectionId: section.id }));
    });
    return list;
  }, [schema]);

  const total = steps.length;
  const isReview = index >= total && total > 0;
  const current = !isReview ? steps[index] : null;

  if (!schema) {
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-foreground/20 border-t-foreground animate-spin" />
      </div>
    );
  }

  const progressPct = isReview ? 100 : Math.round((index / total) * 100);
  const canContinue = current ? stepComplete(current, answers) : true;
  const followups = current ? visibleFollowups(current, answers) : [];

  const goNext = () => {
    if (!canContinue) return;
    setDir(1);
    setIndex((i) => Math.min(i + 1, total));
  };
  const goBack = () => {
    setDir(-1);
    if (index === 0) { navigate("/privacy"); return; }
    setIndex((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await submit();
      navigate("/completato");
    } catch (e) {
      toast.error("Invio non riuscito. Riprova.");
      setSubmitting(false);
    }
  };

  const jumpTo = (qid) => {
    const i = steps.findIndex((q) => q.id === qid);
    if (i >= 0) { setDir(-1); setIndex(i); }
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <header className="w-full border-b border-border/60 sticky top-0 z-20 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto max-w-lg px-6 pt-3.5 pb-3">
          <div className="flex items-center justify-between">
            <button onClick={goBack} className="text-muted-foreground hover:text-foreground transition-colors -ml-1" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <BrandLogo variant="dark" className="h-6" />
            <span className="w-5" />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="fs-eyebrow text-[10px]" data-testid="section-indicator">
              {isReview ? "Riepilogo" : current?.sectionTitle}
            </span>
            <span className="text-xs text-muted-foreground font-medium" data-testid="question-counter">
              {isReview ? "Controlla le risposte" : `Domanda ${index + 1} di ${total}`}
            </span>
          </div>
          <Progress value={progressPct} className="mt-2.5 h-1.5" data-testid="progress-bar" />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-lg px-6 py-8 flex flex-col">
        <AnimatePresence mode="wait" custom={dir}>
          {!isReview ? (
            <motion.div
              key={current.id}
              custom={dir}
              initial={{ opacity: 0, x: dir * 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -24 }}
              transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="flex-1 flex flex-col sm:justify-center sm:-mt-8"
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-600 tracking-tight leading-tight">
                {current.label}
              </h2>
              {HINTS[current.type] && (
                <p className="mt-2 fs-eyebrow text-[10px]" data-testid="answer-hint">{HINTS[current.type]}</p>
              )}
              <div className="mt-7">
                <QuestionInput question={current} value={answers[current.id]} onChange={(v) => setAnswer(current.id, v)} />
              </div>

              {followups.map((f) => (
                <div key={f.id} className="mt-7 pl-4 border-l-2 border-border" data-testid={`followup-${f.id}`}>
                  <p className="text-base font-medium leading-snug mb-3">{f.label}</p>
                  <QuestionInput question={f} value={answers[f.id]} onChange={(v) => setAnswer(f.id, v)} />
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="review"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="flex-1"
            >
              <h2 className="font-heading text-3xl font-700 tracking-tight">Quasi finito</h2>
              <p className="mt-2 text-base text-muted-foreground leading-relaxed">
                Controlla le tue risposte. Puoi modificarle prima dell'invio.
              </p>

              <div className="mt-6 flex flex-col gap-5">
                {schema.sections.map((section) => (
                  <div key={section.id} className="fs-card p-5" data-testid={`review-section-${section.id}`}>
                    <p className="fs-eyebrow text-[10px]">{section.title}</p>
                    <div className="mt-3 flex flex-col divide-y divide-border/70">
                      {section.questions.map((q) => (
                        <div key={q.id} className="py-3 first:pt-0 last:pb-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <p className="text-xs text-muted-foreground leading-snug">{q.label}</p>
                              <p className="mt-0.5 text-sm font-medium break-words">{formatAnswer(q, answers[q.id])}</p>
                            </div>
                            <button onClick={() => jumpTo(q.id)} data-testid={`edit-${q.id}`} className="shrink-0 text-muted-foreground hover:text-foreground transition-colors mt-0.5">
                              <Pencil className="h-4 w-4" />
                            </button>
                          </div>
                          {visibleFollowups(q, answers).map((f) => (
                            <div key={f.id} className="mt-2 pl-3 border-l-2 border-border/70">
                              <p className="text-xs text-muted-foreground leading-snug">{f.label}</p>
                              <p className="mt-0.5 text-sm font-medium break-words">{formatAnswer(f, answers[f.id])}</p>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-8 sticky bottom-6">
          {!isReview ? (
            <Button data-testid="continue-btn" disabled={!canContinue} onClick={goNext} className="w-full h-14 rounded-full text-base font-semibold group disabled:opacity-40">
              Continua
              <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Button>
          ) : (
            <Button data-testid="submit-test-btn" disabled={submitting} onClick={handleSubmit} className="w-full h-14 rounded-full text-base font-semibold group">
              {submitting ? "Invio in corso…" : "Invia il Longevity Test"}
              {!submitting && <Check className="ml-2 h-5 w-5" />}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
